// packages/web/src/lib/hooks/use-progress-sync-gate.ts
/**
 * useProgressSyncGate — wires the progress-cloud-sync opt-in to the
 * updateProgress hook (19BO+ Line 2 B4.4 step 4).
 *
 * This is the activation point for learning-progress field-level cloud sync.
 * It registers (or clears) the module-level progressSyncHook in idb.ts based on
 * two conditions:
 *   1. The user is authenticated (not anonymous).
 *   2. The user's progressCloudSync preference is on.
 *
 * When both hold, makeProgressSyncHook is installed — every updateProgress
 * write then diffs before→after and enqueues ProgressEvents to the sync queue.
 * When either flips, setProgressSyncHook(null) makes updateProgress skip even
 * the structuredClone snapshot (cheap no-op).
 *
 * Mirrors the useSyncEngine pattern: a top-level hook called once in App.tsx
 * that subscribes to currentUser and reacts to auth/pref changes. The hook
 * itself is idempotent — re-registering the same factory shape is harmless.
 *
 * Spec: docs/architecture/llm-native.md §13.4
 */

import { useEffect } from 'react';
import { useStore } from '../store/index.js';
import { setProgressSyncHook } from '../../storage/idb.js';
import { makeProgressSyncHook } from '../progress-sync-hook.js';

export function useProgressSyncGate(): void {
  const currentUser = useStore((s) => s.currentUser);
  const progressCloudSync = useStore((s) => s.progressCloudSync);

  useEffect(() => {
    const isAuthed = currentUser && !currentUser.isAnonymous;
    if (isAuthed && progressCloudSync) {
      // Register the hook with a userId getter that always reads live state.
      // We close over the current user id, but re-register whenever it changes
      // (this effect re-runs on currentUser mutation), so the closure stays fresh.
      setProgressSyncHook(makeProgressSyncHook(() => useStore.getState().currentUser?.id ?? null));
    } else {
      // Opt-in off or anonymous → sync inert (updateProgress skips the snapshot).
      setProgressSyncHook(null);
    }
  }, [currentUser, progressCloudSync]);

  // Cleanup on unmount: clear the hook so a hot-reloaded app doesn't leave a
  // stale hook pointing at a torn-down store.
  useEffect(() => {
    return () => { setProgressSyncHook(null); };
  }, []);
}
